// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import X from "@insite/mobius/Icons/X";
import { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@werner/XDL/constants";
import Add from "@werner/XDL/Icons/Add";
import Exit from "@werner/XDL/Icons/Exit";
import List from "@werner/XDL/Icons/List";
import Print from "@werner/XDL/Icons/Print";
import ProductList from "@werner/XDL/Icons/ProductList";
import Save from "@werner/XDL/Icons/SaveCart";
import Share from "@werner/XDL/Icons/Share";
import Trash from "@werner/XDL/Icons/Trash";
import {
    color,
    createBreadcrumbActionLink,
    createLinkIconStyles,
    ellipsis,
    hideElementOnPrint,
    mergeWithCss,
    sharedProductDescriptionStyles,
    xScreen,
} from "@werner/XDL/Utilities";
import { css } from "styled-components";

interface CartPageStyles {
    cartActions?: {
        saveOrderLink?: LinkPresentationProps;
        addAllToListClickable?: LinkPresentationProps;
        removeAll?: LinkPresentationProps;
    };
    cartLine?: {
        productImage?: ProductImageStyles;
        productBrand?: ProductBrandStyles;
        productDescription?: ProductDescriptionStyles;
        productPartNumbers?: ProductPartNumbersStyles;
        productAvailability?: ProductAvailabilityStyles;
        productPrice?: ProductPriceStyles;
        quantity?: CartLineQuantityStyles;
        totalSmallHeadingAndText?: SmallHeadingAndTextStyles;
        cartLineNotes?: CartLineNotesStyles;
        addToListLink?: LinkPresentationProps;
        removeLink?: LinkPresentationProps;
        totalSmallHeadingAndTextTable?: SmallHeadingAndTextStyles;
    };
    cartPageActions?: {
        shareCart?: LinkPresentationProps;
        printCart?: LinkPresentationProps;
    };
    cartTotalButtons?: {
        buttonsWrapper?: InjectableCss;
        checkoutButton?: ButtonPresentationProps;
        actionButtonsWrapper?: InjectableCss;
        actionButton?: LinkPresentationProps;
    };
}
const linkIconStyles: LinkPresentationProps = {
    color: "secondary",
    typographyProps: {
        size: 15,
        weight: 400,
        css: css`
            letter-spacing: 0;
            line-height: 14px;
        `,
    },
    icon: {
        iconProps: {
            size: 20,
        },
    },
};

const cartPageStyles: CartPageStyles = {
    cartActions: {
        saveOrderLink: {
            ...createLinkIconStyles(Save, linkIconStyles),
        },
        addAllToListClickable: {
            ...createLinkIconStyles(ProductList, linkIconStyles),
        },
        removeAll: {
            ...createLinkIconStyles(Trash, linkIconStyles),
        },
    },
    cartLine: {
        productImage: {
            linkWrappingImage: {
                css: css`
                    width: 100%;
                    height: 100%;
                `,
            },
            img: {
                width: 100,
                height: 100,
                css: css`
                    max-height: 100%;
                    height: auto;
                `,
            },
        },
        productDescription: {
            ...mergeWithCss(sharedProductDescriptionStyles, {
                productDetailDescription: {
                    size: 18,
                    css: css`
                        ${ellipsis(2)}
                        min-height: 44px;
                        margin-bottom: 8px;
                    `,
                },
                productDetailLink: {
                    typographyProps: {
                        size: 18,
                        css: css`
                            ${ellipsis(2)}
                            min-height: 44px;
                            margin-bottom: 8px;
                        `,
                    },
                    css: css`
                        white-space: pre-wrap;
                    `,
                },
            }),
        },
        quantity: {
            readOnlyQuantityText: {
                text: {
                    mergeCss: true,
                    size: 16,
                    weight: 400,
                    css: css`
                        letter-spacing: 0;
                        line-height: 22px;
                    `,
                },
                heading: {
                    mergeCss: true,
                    size: 12,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                        ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                            display: none;
                        }
                    `,
                },
            },
            editableQuantityTextField: {
                cssOverrides: {
                    inputSelect: css`
                        width: 74px;
                        height: 40px;
                        text-align: center;
                    `,
                    formInputWrapper: css`
                        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                            display: flex;
                            flex-direction: column-reverse;
                        }
                    `,
                },
                labelProps: {
                    css: css`
                        display: none;
                        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                            display: block;
                            font-size: 12px;
                            letter-spacing: 0;
                            line-height: 18px;
                            font-weight: 500;
                            color: ${color("text.accent")};
                            margin-bottom: 8px;
                        }
                        ${hideElementOnPrint}
                    `,
                },
            },
        },
        addToListLink: {
            ...createLinkIconStyles(
                Add,
                mergeWithCss(linkIconStyles, {
                    typographyProps: {
                        size: 14,
                    },
                    icon: {
                        iconProps: {
                            size: 8,
                        },
                    },
                }),
            ),
        },
        removeLink: {
            ...createLinkIconStyles(Trash, linkIconStyles),
        },
        productPrice: {
            priceWrapper: {
                css: css`
                    text-align: center;
                `,
            },
            price: {
                priceText: {
                    size: 16,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                        line-height: 22px;
                    `,
                },
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 16,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                        line-height: 22px;
                    `,
                },
            },
            priceLabelText: {
                size: 12,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }
                    ${hideElementOnPrint}
                `,
            },
        },
        totalSmallHeadingAndText: {
            text: {
                size: 16,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 22px;
                    padding-right: 10px;
                    text-align: right;
                `,
            },
            heading: {
                size: 12,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }
                    ${hideElementOnPrint}
                `,
            },
        },
        totalSmallHeadingAndTextTable: {
            text: {
                size: 16,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 22px;
                `,
            },
            heading: {
                css: css`
                    display: none;
                `,
            },
        },
    },
    cartPageActions: {
        shareCart: {
            ...createBreadcrumbActionLink(Share),
        },
        printCart: {
            ...createBreadcrumbActionLink(Print),
        },
    },
    cartTotalButtons: {
        actionButtonsWrapper: {
            css: css`
                display: flex;
                justify-content: space-between;
                margin-top: 18px;
                flex-wrap: wrap;
                gap: 12px;
            `,
        },
        checkoutButton: {
            css: css`
                width: 100%;
            `,
        },
        actionButton: {
            color: "secondary",
            typographyProps: {
                size: 15,
                weight: 400,
                css: css`
                    letter-spacing: 0;
                    line-height: 17px;
                `,
            },
        },
    },
};

export default cartPageStyles;
